/* eslint-disable react/no-unused-prop-types */
import { LangProp } from "components/Header";
import { insideBreakpoint, useBreakpoint } from "utilities/BreakpointProvider";
import { logDebug } from "helpers/generic";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export const emptyMenu = {
  initLevel: 1,
  currentPageParentSlug: "privati",
  breakpoints: ["xl"],
  langs: [
    {
      ctaType: "internalLink",
      internalLink: {
        node_locale: "it",
        slug: "privati",
      },
      label: "it",
      link: "/it/privati",
    },
    {
      label: "en",
      ctaType: "internalLink",
      internalLink: {
        node_locale: "en",
        slug: "private",
      },
      link: "/en/private",
    },
  ],
  privateArea: undefined,
  props: {},

  toggleMenu: () => {},
  nextMenuMobile: () => {},
  backMenuMobile: () => {},
  toggleSearch: () => {},
  searchOpen: false,
};

const defaultValue = {
  ...emptyMenu,
};

interface MenuProviderInitProps {
  children: React.ReactNode;
  currentPageParentSlug: string;
  langs: LangProp[];
  initLevel: number;
  // TODO: ts cta reference
  privateArea?: any;

  toggleMenu?: (element: HTMLButtonElement) => void;
  nextMenuMobile?: (e: React.MouseEvent) => void;
  backMenuMobile?: VoidFunction;
  toggleSearch?: VoidFunction;
  searchOpen?: boolean;
}

interface MenuProviderProps extends Omit<MenuProviderInitProps, "children"> {}

const MenuContext = createContext<MenuProviderProps>(defaultValue);

const MenuProvider = ({
  children,
  currentPageParentSlug,
  langs,
  initLevel,
  privateArea,
}: MenuProviderInitProps) => {
  // UPDATE emptyMenu with props

  // GET PARENET PAGE FOT MENU TOP
  // TODO: regex /lang/page/page-child -> page
  // const currentPageParentSlug = 'business';
  if (langs) {
    emptyMenu.langs = langs;
  }
  if (currentPageParentSlug) {
    emptyMenu.currentPageParentSlug = currentPageParentSlug;
  }
  if (privateArea) {
    emptyMenu.privateArea = privateArea;
  }

  if (initLevel !== undefined) {
    emptyMenu.initLevel = initLevel;
  }

  const [menuProps, setMenuProps] = useState(emptyMenu);
  const breakpoints = useBreakpoint();
  const [searchOpen, setSearchOpen] = useState(false);

  const htmlTag = useRef<HTMLHtmlElement | null>(null);
  const bodyTag = useRef<HTMLBodyElement | null>(null);
  const header = useRef<HTMLDivElement | null>(null);
  const elNavLevel0 = useRef<HTMLDivElement | null>(null);
  const headerTopBreadcrumb = useRef<HTMLDivElement | null>(null);
  const headerTopBreadcrumbValue = useRef<HTMLDivElement | null>(null);
  const menuNavBreadcrumb = useRef<HTMLDivElement | null>(null);
  const menuNavBreadcrumbValue = useRef<HTMLDivElement | null>(null);
  const initListenerBackTransition = useRef(true);
  const eventBackMenuMobile = useRef(false);
  const currentLevelUpdated = useRef<number>(0);
  const headerMenuToggle = useRef<HTMLButtonElement | null>(null);

  const checkRemoveClassMenuItemOpen = (e: TransitionEvent) => {
    if (
      header.current &&
      eventBackMenuMobile.current &&
      e.target === elNavLevel0.current
    ) {
      const currentEl = header.current.querySelector(
        `.menuItem-level-${currentLevelUpdated.current}.menuItem--open`,
      );
      if (currentEl) {
        currentEl.classList.remove("menuItem--open");
      }
      eventBackMenuMobile.current = false;
    }

    initListenerBackTransition.current = false;
  };

  useEffect(() => {
    htmlTag.current = document.querySelector("html");
    bodyTag.current = document.querySelector("body");
    header.current = document.querySelector("header.header");
    if (!header.current || !bodyTag.current) return undefined;

    elNavLevel0.current = header.current.querySelector(".menuNav-level-0");
    headerMenuToggle.current = header.current.querySelector(".header__toggle");
    bodyTag.current.classList.remove("no-scroll-menu-opened");

    return () => {
      if (!elNavLevel0.current) return undefined;
      elNavLevel0.current.removeEventListener(
        "transitionend",
        checkRemoveClassMenuItemOpen,
        false,
      );
      return undefined;
    };
  }, []);

  logDebug("%cUSE MENU PROVIDER", "color:green");

  const getLevelActive = () => {
    if (!elNavLevel0.current) return 0;
    const val = elNavLevel0.current.getAttribute("data-level");
    return val !== null ? parseInt(val, 10) : 0;
  };

  const updateElLevel0 = useCallback(
    (level: number, next: boolean = true, reset: boolean = false) => {
      if (!elNavLevel0.current) return 0;
      let currentLevel = level;
      elNavLevel0.current.classList.remove(
        `menuNav-transition-${currentLevel}`,
      );
      if (!reset) {
        if (next) {
          currentLevel += 1;
        } else {
          currentLevel -= 1;
        }
      } else {
        currentLevel = menuProps.initLevel;
      }

      elNavLevel0.current.classList.add(`menuNav-transition-${currentLevel}`);
      elNavLevel0.current.setAttribute("data-level", currentLevel.toString());
      return currentLevel;
    },
    [menuProps.initLevel],
  );

  const nextMenuMobile = useCallback(
    (e: React.MouseEvent) => {
      logDebug("click NEXT", e.target);
      // NOTE: check propriety Element.prototype.closest for airbrake

      if (
        // @ts-expect-error
        insideBreakpoint(breakpoints, menuProps.breakpoints, false) &&
        // @ts-expect-error
        Element.prototype.closest
      ) {
        const currentEl = e.target as HTMLDivElement;
        // TODO: Check ie11 closest, capire se babel crea il polyfill https://developer.mozilla.org/it/docs/Web/API/Element/closest
        const parentCurrentEl = currentEl.closest(
          ".menuItem",
        ) as HTMLDivElement;
        // @ts-expect-error
        logDebug("c%closest", "color:green", parentCurrentEl);
        // NOTE: the following piece of code is responsible for the prevention of click event on top level menu items with submenu
        if (
          parentCurrentEl &&
          parentCurrentEl.classList.contains("menuItem--has-submenu")
        ) {
          e.preventDefault();

          parentCurrentEl.classList.add("menuItem--open");
          const levelActive = getLevelActive();

          if (levelActive && levelActive >= menuProps.initLevel) {
            updateElLevel0(levelActive);
          }
        }
      }
    },
    [breakpoints, menuProps.breakpoints, menuProps.initLevel, updateElLevel0],
  );

  const backMenuMobile = useCallback(() => {
    if (!elNavLevel0.current || !header.current) return;
    const levelActive = getLevelActive();
    logDebug("click BACK", levelActive);
    if (levelActive && levelActive > menuProps.initLevel) {
      eventBackMenuMobile.current = true;
      currentLevelUpdated.current = updateElLevel0(levelActive, false);
      if (initListenerBackTransition.current) {
        elNavLevel0.current.addEventListener(
          "transitionend",
          checkRemoveClassMenuItemOpen,
          false,
        );
      }
    }
  }, [menuProps.initLevel, updateElLevel0]);

  const closeAllElementsMenuItemsOpend = useCallback(() => {
    if (!elNavLevel0.current) return;
    const menuItemLevel0 = header.current?.querySelector(
      ".menuItem-level-1.menuItem--open",
    );

    if (menuItemLevel0) {
      menuItemLevel0.classList.remove("menuItem--open");
    }
    elNavLevel0.current.removeEventListener(
      "transitionend",
      closeAllElementsMenuItemsOpend,
      true,
    );
  }, []);

  const resetMenuMobile = useCallback(
    (levelActive: number) => {
      if (!elNavLevel0.current) return;
      updateElLevel0(levelActive, undefined, true);

      elNavLevel0.current.addEventListener(
        "transitionend",
        closeAllElementsMenuItemsOpend,
        true,
      );
    },
    [closeAllElementsMenuItemsOpend, updateElLevel0],
  );

  const toggleMenu = useCallback(
    (element: HTMLButtonElement) => {
      if (!bodyTag.current || !header.current) return;
      // NOTE: check propriety Element.prototype.closest for airbrake
      // @ts-expect-error closest
      if (Element.prototype.closest) {
        const toggleBtn = element.closest(".header__toggle");
        if (toggleBtn) {
          toggleBtn.classList.toggle("header__toggle--open");
        }
        const menuOpenStatus = header.current.classList.toggle("menu--open");
        setSearchOpen(false);
        if (
          bodyTag.current.classList.contains("no-scroll-menu-opened") &&
          !searchOpen
        ) {
          bodyTag.current.classList.remove("no-scroll-menu-opened");
        } else {
          bodyTag.current.classList.add("no-scroll-menu-opened");
        }
        logDebug("toggle open menu->>", menuOpenStatus);
        if (!menuOpenStatus) {
          const levelActive = getLevelActive();
          resetMenuMobile(levelActive);
        }
      }
    },
    [resetMenuMobile, searchOpen],
  );

  const toggleSearch = useCallback(() => {
    if (
      headerMenuToggle.current &&
      header.current &&
      header.current.classList.contains("menu--open")
    ) {
      toggleMenu(headerMenuToggle.current);
    }
    setSearchOpen(!searchOpen);
  }, [searchOpen, toggleMenu]);

  useEffect(() => {
    if (htmlTag.current && bodyTag.current) {
      if (searchOpen) {
        htmlTag.current.classList.add("no-scroll-search-opened");
        bodyTag.current.classList.add("no-scroll-search-opened");
      } else {
        htmlTag.current.classList.remove("no-scroll-search-opened");
        bodyTag.current.classList.remove("no-scroll-search-opened");
      }
    }
  }, [searchOpen]);

  const value = useMemo(
    () => ({
      ...menuProps,
      toggleMenu,
      nextMenuMobile,
      backMenuMobile,
      toggleSearch,
      searchOpen,
    }),
    [
      toggleMenu,
      nextMenuMobile,
      backMenuMobile,
      toggleSearch,
      searchOpen,
      menuProps,
    ],
  );

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
};

const useMenu = () => useContext(MenuContext);

export { MenuProvider, useMenu };
